import type {
  CompanyProfileDTO,
  CompanyProfileUpdateRequest,
} from "../../api/companyProfile"

export type CompanyProfileDraft = Omit<
  CompanyProfileUpdateRequest,
  "expectedRevisionNumber"
>

export type CompanyProfileField = keyof CompanyProfileDraft
export type CompanyProfileErrors = Partial<Record<CompanyProfileField, string>>

export const companyProfileFieldMaxLengths: Partial<
  Record<CompanyProfileField, number>
> = {
  shortName: 200,
  legalName: 500,
  wmsName: 200,
  regon: 14,
  krs: 10,
  bdo: 20,
  email: 320,
  phone: 32,
  website: 2048,
  addressLine1: 500,
  addressLine2: 500,
  postalCode: 16,
  city: 200,
  countryCode: 2,
  locale: 32,
  timezone: 64,
  labelLogo: 2048,
  labelLogoAlt: 500,
  changeReason: 1000,
}

export const profileToDraft = (
  profile: CompanyProfileDTO,
): CompanyProfileDraft => ({
  shortName: profile.shortName,
  legalName: profile.legalName,
  wmsName: profile.wmsName,
  taxId: profile.taxId,
  regon: profile.regon,
  krs: profile.krs,
  bdo: profile.bdo ?? "",
  email: profile.email,
  phone: profile.phone,
  website: profile.website,
  addressLine1: profile.address.line1,
  addressLine2: profile.address.line2,
  postalCode: profile.address.postalCode,
  city: profile.address.city,
  countryCode: profile.address.countryCode,
  locale: profile.locale,
  timezone: profile.timezone,
  labelLogo: profile.assets.labelLogo,
  labelLogoSource:
    profile.labelLogoSource ??
    (profile.assets.labelLogo.startsWith("https://") ? "url" : "default"),
  labelLogoAlt: profile.alt.labelLogo,
  changeReason: "",
})

export const normalizePolishNip = (value: string) => value.replace(/[\s-]/g, "")

export function isValidPolishNip(value: string) {
  const normalized = normalizePolishNip(value)
  if (!/^\d{10}$/.test(normalized)) return false
  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
  const checksum =
    weights.reduce(
      (sum, weight, index) => sum + Number(normalized[index]) * weight,
      0,
    ) % 11
  return checksum !== 10 && checksum === Number(normalized[9])
}

export function isSafeWebsiteUrl(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return true
  try {
    const parsed = new URL(trimmed)
    return (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      Boolean(parsed.hostname)
    )
  } catch {
    return false
  }
}

function isPrivateIPv4(numbers: number[]) {
  return (
    numbers[0] === 0 ||
    numbers[0] === 10 ||
    numbers[0] === 127 ||
    (numbers[0] === 169 && numbers[1] === 254) ||
    (numbers[0] === 172 && numbers[1] >= 16 && numbers[1] <= 31) ||
    (numbers[0] === 192 && numbers[1] === 168)
  )
}

function isPrivateOrLocalHost(hostname: string) {
  const host = hostname.toLowerCase().replace(/^\[|\]$/g, "")
  if (host === "localhost" || host.endsWith(".localhost")) return true

  const octets = host.split(".")
  if (octets.length === 4 && octets.every((part) => /^\d{1,3}$/.test(part))) {
    const numbers = octets.map(Number)
    return numbers.every((part) => part <= 255) && isPrivateIPv4(numbers)
  }

  const mappedIPv4 = /^::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/.exec(host)
  if (mappedIPv4) {
    const high = Number.parseInt(mappedIPv4[1], 16)
    const low = Number.parseInt(mappedIPv4[2], 16)
    return isPrivateIPv4([high >> 8, high & 0xff, low >> 8, low & 0xff])
  }

  if (!host.includes(":")) return false
  return (
    host === "::" ||
    host === "::1" ||
    host.startsWith("fc") ||
    host.startsWith("fd") ||
    /^fe[89ab]/.test(host)
  )
}

export function isSafeLogoUrl(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return true
  try {
    const parsed = new URL(trimmed)
    return (
      parsed.protocol === "https:" &&
      Boolean(parsed.hostname) &&
      !parsed.username &&
      !parsed.password &&
      !isPrivateOrLocalHost(parsed.hostname)
    )
  } catch {
    return false
  }
}

const trimmed = (value: string) => value.trim()

export function canonicalizeCompanyProfileDraft(
  draft: CompanyProfileDraft,
): CompanyProfileDraft {
  return {
    ...draft,
    shortName: trimmed(draft.shortName),
    legalName: trimmed(draft.legalName),
    wmsName: trimmed(draft.wmsName),
    taxId: draft.taxId.trim() ? normalizePolishNip(draft.taxId) : "",
    regon: trimmed(draft.regon),
    krs: trimmed(draft.krs),
    bdo: trimmed(draft.bdo),
    email: trimmed(draft.email),
    phone: trimmed(draft.phone),
    website: trimmed(draft.website),
    addressLine1: trimmed(draft.addressLine1),
    addressLine2: trimmed(draft.addressLine2),
    postalCode: trimmed(draft.postalCode),
    city: trimmed(draft.city),
    countryCode: trimmed(draft.countryCode).toUpperCase(),
    locale: trimmed(draft.locale),
    timezone: trimmed(draft.timezone),
    labelLogo: trimmed(draft.labelLogo),
    labelLogoAlt: trimmed(draft.labelLogoAlt),
    changeReason: trimmed(draft.changeReason),
  }
}

export function isCompanyProfileDirty(
  baseline: CompanyProfileDraft,
  draft: CompanyProfileDraft,
) {
  const normalizedBaseline = canonicalizeCompanyProfileDraft({
    ...baseline,
    changeReason: "",
  })
  const normalizedDraft = canonicalizeCompanyProfileDraft({
    ...draft,
    changeReason: "",
  })
  return JSON.stringify(normalizedBaseline) !== JSON.stringify(normalizedDraft)
}

export function validateCompanyProfileDraft(
  draft: CompanyProfileDraft,
  requireReason: boolean,
): CompanyProfileErrors {
  const errors: CompanyProfileErrors = {}
  if (!draft.shortName.trim()) errors.shortName = "Krótka nazwa jest wymagana."
  if (!draft.legalName.trim()) errors.legalName = "Nazwa prawna jest wymagana."
  if (!draft.wmsName.trim()) errors.wmsName = "Nazwa WMS jest wymagana."
  if (draft.taxId.trim() && !isValidPolishNip(draft.taxId)) {
    errors.taxId = "Wprowadź prawidłowy polski NIP."
  }
  if (draft.regon.trim() && !/^(?:\d{9}|\d{14})$/.test(draft.regon.trim())) {
    errors.regon = "REGON musi zawierać 9 lub 14 cyfr."
  }
  if (draft.krs.trim() && !/^\d{10}$/.test(draft.krs.trim())) {
    errors.krs = "KRS musi zawierać 10 cyfr."
  }
  if (
    draft.bdo.trim() &&
    (!/^\d+$/.test(draft.bdo.trim()) || draft.bdo.trim().length > 20)
  ) {
    errors.bdo = "BDO musi zawierać cyfry i mieć maksymalnie 20 znaków."
  }
  if (
    draft.email.trim() &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())
  ) {
    errors.email = "Wprowadź prawidłowy adres e-mail."
  }
  if (draft.website.trim() && !isSafeWebsiteUrl(draft.website)) {
    errors.website = "Adres strony musi zaczynać się od http:// lub https://."
  }
  if (draft.phone.trim() && !/^[0-9+(). /-]{3,32}$/.test(draft.phone.trim())) {
    errors.phone = "Wprowadź prawidłowy numer telefonu."
  }
  if (
    draft.postalCode.trim() &&
    !/^[A-Za-z0-9 -]{2,16}$/.test(draft.postalCode.trim())
  ) {
    errors.postalCode = "Wprowadź prawidłowy kod pocztowy."
  }
  if (!/^[A-Za-z]{2}$/.test(draft.countryCode.trim())) {
    errors.countryCode = "Kod kraju musi mieć dwie litery."
  }
  if (!draft.locale.trim()) errors.locale = "Wybierz ustawienia językowe."
  if (!draft.timezone.trim()) errors.timezone = "Wybierz strefę czasową."
  if (draft.labelLogoSource === "url" && !isSafeLogoUrl(draft.labelLogo)) {
    errors.labelLogo = "Podaj bezpośredni, publiczny adres HTTPS obrazu."
  }
  if (requireReason && !draft.changeReason.trim()) {
    errors.changeReason = "Opisz powód zmiany."
  }
  for (const [field, maxLength] of Object.entries(
    companyProfileFieldMaxLengths,
  ) as Array<[CompanyProfileField, number]>) {
    if (Array.from(draft[field].trim()).length > maxLength && !errors[field]) {
      errors[field] = `Maksymalna długość to ${maxLength} znaków.`
    }
  }
  return errors
}

export function draftToUpdateRequest(
  draft: CompanyProfileDraft,
  expectedRevisionNumber: number,
): CompanyProfileUpdateRequest {
  return {
    ...canonicalizeCompanyProfileDraft(draft),
    expectedRevisionNumber,
  }
}
